import { ReactElement, useContext, useEffect, useMemo } from "react";
import { Link, useHistory, useLocation } from 'react-router-dom';
import classNames from "classnames";
import LayoutContext from "../../../contexts/LayoutContext";
import AuthContext from "../../../contexts/AuthContext";
import NavegacionContext from "../../../contexts/NavegacionContext";
import getMenuBarItems from "../../../utils/menubar";
import { getPantallaActual, getPantallas } from "../../../utils/menuGroups";
import { TramoRuta, armarRuta, describirPagina } from "../../../utils/rutaNavegacion";
import { BreadcrumbItem } from "../../../models/layout/BreadcrumbItem";

const SIN_ITEMS: BreadcrumbItem[] = [];

/**
 * Botón Volver y ruta de la página. La ruta se arma sola desde el menú
 * (Inicio / Bloque / Sección / Pantalla) más los tramos que entregue la
 * página; el bloque y la sección abren el menú lateral en ese lugar.
 */
const HeaderBreadcrumb = (): ReactElement => {
    const { breadcrumbItems, breadcrumbPath, headerTitle, abrirMenu } = useContext(LayoutContext);
    const { userRoles } = useContext(AuthContext);
    const { anterior, registrarTitulo } = useContext(NavegacionContext);
    const history = useHistory();
    const { pathname, search } = useLocation();

    const pantallas = useMemo(() => getPantallas(getMenuBarItems(userRoles)), [userRoles]);
    // Los tramos que dejó otra página no valen aquí (páginas que no llaman a useBreadcrumb).
    const itemsPagina = breadcrumbPath === pathname ? breadcrumbItems : SIN_ITEMS;
    const tramos = useMemo(
        () => armarRuta(getPantallaActual(pantallas, pathname), pathname, itemsPagina, headerTitle),
        [pantallas, pathname, itemsPagina, headerTitle]
    );

    const { titulo, detalle } = describirPagina(tramos);
    useEffect(() => {
        registrarTitulo(`${pathname}${search}`, titulo, detalle);
    }, [registrarTitulo, pathname, search, titulo, detalle]);

    // Sin página anterior en el ERP (se entró por un link o se recargó): sube un nivel.
    const padre = [...tramos.slice(0, -1)].reverse().find((t) => t.href && t.href !== pathname);
    const volver = (): void => {
        if (anterior) history.goBack();
        else if (padre?.href) history.push(padre.href);
    };
    const destinoVolver = anterior ? anterior.titulo || 'la página anterior' : padre?.label;

    // En pantallas chicas se ven solo los dos últimos tramos; el resto queda en «…».
    const ocultos = tramos.length > 3 ? tramos.slice(0, -2) : [];
    const bloqueOculto = ocultos.find((t) => t.abrirMenu)?.abrirMenu;

    const renderTramo = (tramo: TramoRuta, i: number): ReactElement => {
        const esActual = i === tramos.length - 1;
        const clase = classNames('ruta-tramo', { 'ruta-oculta-movil': i < tramos.length - 2 && tramos.length > 3 });
        let contenido: ReactElement | string = tramo.label;
        if (esActual) {
            contenido = <span aria-current="page">{tramo.label}</span>;
        } else if (tramo.href) {
            contenido = <Link to={tramo.href}>{tramo.label}</Link>;
        } else if (tramo.abrirMenu) {
            const { group, seccion } = tramo.abrirMenu;
            contenido = (
                <button type="button" onClick={() => abrirMenu(group, seccion)} title={`Ver ${tramo.label} en el menú`}>
                    {tramo.label}
                </button>
            );
        }
        return <li key={`${i}-${tramo.label}`} className={clase}>{contenido}</li>;
    };

    return (
        <div className="ruta-navegacion">
            {pathname !== '/' && destinoVolver && (
                <button
                    type="button"
                    className="btn-volver"
                    onClick={volver}
                    title={`Volver a ${destinoVolver}`}
                    aria-label={`Volver a ${destinoVolver}`}
                >
                    <i className="fa fa-arrow-left" aria-hidden="true" />
                    <span className="btn-volver-texto">Volver a {destinoVolver}</span>
                </button>
            )}
            <nav aria-label="Ruta de la página" className="ruta">
                <ol>
                    {ocultos.length > 0 && (
                        <li className="ruta-tramo ruta-elipsis">
                            {bloqueOculto ? (
                                <button
                                    type="button"
                                    onClick={() => abrirMenu(bloqueOculto.group, bloqueOculto.seccion)}
                                    title={ocultos.map((t) => t.label).join(' / ')}
                                    aria-label={`Ruta: ${ocultos.map((t) => t.label).join(' / ')}`}
                                >
                                    …
                                </button>
                            ) : (
                                <span title={ocultos.map((t) => t.label).join(' / ')}>…</span>
                            )}
                        </li>
                    )}
                    {tramos.map(renderTramo)}
                </ol>
            </nav>
        </div>
    );
};

export default HeaderBreadcrumb;
